'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpToLine, MoveDiagonal2, VideoOff } from 'lucide-react';
import { MediaPlayer } from '../media-player/index.jsx';
import { MuxVideoPlayer } from '../mux-video-player/index.jsx';
import { Button } from '../../ui/button.jsx';
import { cn } from '../../../lib/utils.js';
var ASPECT_RATIO = 9 / 16; // height = width * ASPECT_RATIO
var MIN_WIDTH = 280;
var DEFAULT_WIDTH = 360;
var MARGIN = 12;
var PLAYER_CHROME_HEIGHT = 72;
var KEYBOARD_STEP = 20;
var chromeButtonClassName = 'text-muted-foreground/60 hover:bg-muted/80 hover:text-muted-foreground';
export function FloatingVideoPlayer(_a) {
    var visible = _a.visible, livePlaybackId = _a.livePlaybackId, liveSrc = _a.liveSrc, isStreamActive = _a.isStreamActive, recordingPlaybackId = _a.recordingPlaybackId, recordingSrc = _a.recordingSrc, envKey = _a.envKey, recordingSignedUrl = _a.recordingSignedUrl, poster = _a.poster, meetingId = _a.meetingId, meetingTitle = _a.meetingTitle, nowLabel = _a.nowLabel, playerRef = _a.playerRef, onTimeUpdate = _a.onTimeUpdate, onCaptionLanguageChange = _a.onCaptionLanguageChange, preferredCaptionLanguage = _a.preferredCaptionLanguage, captionsMenuNote = _a.captionsMenuNote, className = _a.className, forceAudioOnly = _a.forceAudioOnly, _b = _a.defaultAnchor, defaultAnchor = _b === void 0 ? 'bottom-right' : _b, onHide = _a.onHide, onDock = _a.onDock;
    var containerRef = useRef(null);
    var _c = useState({ x: -1, y: -1 }), pos = _c[0], setPos = _c[1];
    var _d = useState(DEFAULT_WIDTH), width = _d[0], setWidth = _d[1];
    // ─── Initial positioning ──────────────────────────────────────────────────
    useEffect(function () {
        if (pos.x === -1) {
            var y = window.innerHeight -
                Math.round(DEFAULT_WIDTH * ASPECT_RATIO) -
                MARGIN -
                PLAYER_CHROME_HEIGHT;
            var x = defaultAnchor === 'bottom-left' ? MARGIN : window.innerWidth - DEFAULT_WIDTH - MARGIN;
            setPos({ x: x, y: y });
        }
    }, [defaultAnchor, pos.x]);
    // ─── Helpers ───────────────────────────────────────────────────────────────
    var clampPos = useCallback(function (x, y, w) { return ({
        x: Math.max(MARGIN, Math.min(x, window.innerWidth - w - MARGIN)),
        y: Math.max(MARGIN, Math.min(y, window.innerHeight - Math.round(w * ASPECT_RATIO) - MARGIN - PLAYER_CHROME_HEIGHT)),
    }); }, []);
    var clampWidth = useCallback(function (w, x) { return Math.min(Math.max(MIN_WIDTH, w), window.innerWidth - x - MARGIN); }, []);
    // ─── Drag (title bar) ─────────────────────────────────────────────────────
    var dragState = useRef(null);
    var handleDragStart = useCallback(function (e) {
        e.preventDefault();
        dragState.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
        var onMove = function (ev) {
            if (!dragState.current)
                return;
            var dx = ev.clientX - dragState.current.startX;
            var dy = ev.clientY - dragState.current.startY;
            setPos(clampPos(dragState.current.origX + dx, dragState.current.origY + dy, width));
        };
        var onUp = function () {
            dragState.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [pos.x, pos.y, width, clampPos]);
    // ─── NW resize (top-left of title bar) — width drives everything ──────────
    var resizeNWState = useRef(null);
    var handleNWResizeStart = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        var origBottom = pos.y + Math.round(width * ASPECT_RATIO);
        resizeNWState.current = { startX: e.clientX, origX: pos.x, origWidth: width, origBottom: origBottom };
        var onMove = function (ev) {
            if (!resizeNWState.current)
                return;
            var s = resizeNWState.current;
            var dx = ev.clientX - s.startX; // negative = dragging left = bigger
            var rawWidth = s.origWidth - dx;
            // Cap width so the top edge never goes above the margin
            var maxWidthByTop = Math.floor((s.origBottom - MARGIN) / ASPECT_RATIO);
            var newWidth = Math.max(MIN_WIDTH, Math.min(rawWidth, maxWidthByTop));
            // Left edge moves by how much width actually changed
            var widthDelta = s.origWidth - newWidth;
            var newX = s.origX + widthDelta;
            // Bottom edge stays fixed — top expands upward
            var newY = s.origBottom - Math.round(newWidth * ASPECT_RATIO);
            setWidth(clampWidth(newWidth, newX));
            setPos(clampPos(newX, newY, newWidth));
        };
        var onUp = function () {
            resizeNWState.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [pos.x, pos.y, width, clampPos, clampWidth]);
    // ─── Keyboard handlers ─────────────────────────────────────────────────────
    var handleDragKeyDown = useCallback(function (e) {
        var deltas = {
            ArrowLeft: [-KEYBOARD_STEP, 0],
            ArrowRight: [KEYBOARD_STEP, 0],
            ArrowUp: [0, -KEYBOARD_STEP],
            ArrowDown: [0, KEYBOARD_STEP],
        };
        var delta = deltas[e.key];
        if (!delta)
            return;
        e.preventDefault();
        setPos(function (p) { return clampPos(p.x + delta[0], p.y + delta[1], width); });
    }, [clampPos, width]);
    var handleNWResizeKeyDown = useCallback(function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')
            return;
        e.preventDefault();
        e.stopPropagation();
        var origBottom = pos.y + Math.round(width * ASPECT_RATIO);
        var delta = e.key === 'ArrowLeft' ? KEYBOARD_STEP : -KEYBOARD_STEP;
        var maxWidthByTop = Math.floor((origBottom - MARGIN) / ASPECT_RATIO);
        var newWidth = Math.max(MIN_WIDTH, Math.min(width + delta, maxWidthByTop));
        var newX = pos.x + (width - newWidth);
        var newY = origBottom - Math.round(newWidth * ASPECT_RATIO);
        setWidth(clampWidth(newWidth, newX));
        setPos(clampPos(newX, newY, newWidth));
    }, [width, pos.x, pos.y, clampPos, clampWidth]);
    // ─── Clamp on window resize ────────────────────────────────────────────────
    useEffect(function () {
        var onResize = function () { return setPos(function (p) { return clampPos(p.x, p.y, width); }); };
        window.addEventListener('resize', onResize);
        return function () { return window.removeEventListener('resize', onResize); };
    }, [width, clampPos]);
    if (!visible || pos.x === -1)
        return null;
    var isLive = !!livePlaybackId && !!isStreamActive;
    return (<div ref={containerRef} className={cn('border-border fixed z-50 overflow-hidden rounded-sm border-2 shadow-2xl', className)} style={{
            left: pos.x,
            top: pos.y,
            width: width,
        }} aria-label="Floating video player">
      {/* ── Title bar (draggable) ── */}
      <div role="toolbar" tabIndex={0} aria-label="Video player controls — arrow keys to move" className="border-border bg-muted flex cursor-grab items-center gap-2 border-b px-2 py-1.5 active:cursor-grabbing" onMouseDown={handleDragStart} onKeyDown={handleDragKeyDown}>
        {/* NW resize handle */}
        <Button aria-label="Resize video player — left/right arrow keys to resize" className={cn('cursor-nw-resize', chromeButtonClassName)} onKeyDown={handleNWResizeKeyDown} onMouseDown={function (event) {
            event.stopPropagation();
            handleNWResizeStart(event);
        }} size="icon-sm" tooltip={{ content: 'Resize player' }} type="button" variant="ghost">
          <MoveDiagonal2 aria-hidden="true"/>
        </Button>

        <span className="text-foreground min-w-0 flex-1 truncate text-center text-xs font-medium">
          {meetingTitle}
        </span>

        {onDock && (<Button aria-label="Dock recording" className={chromeButtonClassName} onClick={onDock} onMouseDown={function (event) { return event.stopPropagation(); }} size="icon-sm" tooltip={{ content: 'Dock recording' }} type="button" variant="ghost">
            <ArrowUpToLine aria-hidden="true"/>
          </Button>)}

        {onHide && (<Button aria-label="Hide recording" className={chromeButtonClassName} onClick={onHide} onMouseDown={function (event) { return event.stopPropagation(); }} size="icon-sm" tooltip={{ content: 'Hide recording' }} type="button" variant="ghost">
            <VideoOff aria-hidden="true"/>
          </Button>)}
      </div>

      {/* ── Video (overflow-hidden + rounded-none prevents wing artifacts) ── */}
      <div className="aspect-video overflow-hidden rounded-none bg-black">
        {isLive ? (<MuxVideoPlayer ref={playerRef} playbackId={livePlaybackId} src={liveSrc} envKey={envKey} streamType="live" isStreamActive={true} autoplay muted onTimeUpdate={onTimeUpdate} onCaptionLanguageChange={onCaptionLanguageChange} preferredCaptionLanguage={preferredCaptionLanguage} captionsMenuNote={captionsMenuNote} metadata={{
                videoId: "meeting-live-".concat(meetingId),
                videoTitle: "".concat(meetingTitle, " Live Stream"),
            }}/>) : recordingPlaybackId ? (<MuxVideoPlayer ref={playerRef} forceAudioOnly={forceAudioOnly} playbackId={recordingPlaybackId} src={recordingSrc} envKey={envKey} poster={poster} streamType="on-demand" onTimeUpdate={onTimeUpdate} onCaptionLanguageChange={onCaptionLanguageChange} preferredCaptionLanguage={preferredCaptionLanguage} captionsMenuNote={captionsMenuNote} metadata={{
                videoId: "meeting-".concat(meetingId),
                videoTitle: meetingTitle,
            }}/>) : recordingSignedUrl ? (<MediaPlayer ref={playerRef} poster={poster} src={recordingSignedUrl} onTimeUpdate={onTimeUpdate}/>) : null}
      </div>

      {/* ── Now: bar ── */}
      <div className="border-border bg-muted flex items-center gap-1.5 border-t px-3 py-1.5">
        <span className="text-foreground shrink-0 text-xs font-semibold tracking-wide uppercase">
          Now:
        </span>
        <span className="text-foreground min-w-0 flex-1 truncate text-xs">
          {nowLabel !== null && nowLabel !== void 0 ? nowLabel : <span className="italic">No active item</span>}
        </span>
      </div>
    </div>);
}
